import React,{ useState, useEffect, useRef } from 'react';
import { downloadCsv } from '../common/sharedFunctions';
import MaterialTable from "material-table";
import { useSelector, useDispatch } from "react-redux";
import CircularLoading from "../components/CircularLoading";
import { api } from 'common';
import { useTranslation } from "react-i18next";
import moment from 'moment/min/moment-with-locales';
import {
  Modal,
  Grid,
  Typography,
  Chip,
  Box,
  Avatar,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import Button from "components/CustomButtons/Button.js";
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CircularProgress from '@mui/material/CircularProgress';
import { makeStyles } from '@mui/styles';
import AlertDialog from '../components/AlertDialog';
import { modernTableStyle, modernTableOptions } from '../common/tableTheme';
import UserCreateDialog from '../components/UserCreateDialog';

const useStyles = makeStyles((theme) => ({
  heading:{
   marginBottom:20,
   height:'10%'
  },
  submit1: {
    marginLeft:30 
  },
  submit: {
    backgroundColor: '#2563eb',
  },
  submit5: {
    backgroundColor: '#0f172a',
  },
  submit3:{
    width:'100%',
    borderRadius: '10px',
    marginTop: 12,
    padding: '10px 16px',
    backgroundColor: '#2563eb',
    color: '#ffffff',
    fontWeight: 600,
    textTransform: 'none'
  },
  modal: {
    display: 'flex',
    padding: theme.spacing(2),
    alignItems: 'center',
    justifyContent: 'center',
  },
  paper: {
    width: 480,
    maxWidth: '92vw',
    backgroundColor: '#ffffff',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
    padding: theme.spacing(3),
    borderRadius: '16px',
    border: '1px solid #e2e8f0',
    alignItems: 'center',
    justifyContent: 'center',
    outline: 'none'
  },
  submit4:{
    backgroundColor: '#dc2626',
    width:'100%',
    borderRadius: '10px',
    marginTop: 12,
    padding: '10px 16px',
    color: '#ffffff',
    fontWeight: 600,
    textTransform: 'none'
  }
}));

export default function Users() {
  const { t,i18n } = useTranslation();
  const isRTL = i18n.dir();
  const {
    addUser,
    editUser, 
    deleteUser,
    checkUserExists,
    fetchUsersOnce,
    updateLicenseImage
  } = api;
  const [data, setData] = useState([]);
  const staticusers = useSelector(state => state.usersdata.staticusers || state.usersdata.users);
  const settings = useSelector(state => state.settingsdata.settings);
  const auth = useSelector(state => state.auth);
  const dispatch = useDispatch();
  const loaded = useRef(false);
  const classes = useStyles();
  const rootRef = useRef(null);

  // Modais de edição e exclusão
  const [editingUser, setEditingUser] = useState(null);
  const [deleteConfirmUser, setDeleteConfirmUser] = useState(null);

  useEffect(()=>{
    dispatch(fetchUsersOnce());
  },[dispatch,fetchUsersOnce]);
  
  useEffect(()=>{
    if(staticusers && Array.isArray(staticusers)){
      const profile = auth.profile;
      const tId = profile?.tenant_id || profile?.tenantId;
      const isSuper = Boolean(
        profile?.is_superadmin || 
        profile?.role === 'superadmin' || 
        profile?.usertype === 'superadmin' || 
        profile?.usertype === 'admin'
      );

      setData(staticusers.filter(user => {
        const isCustomer = user.usertype === 'customer' || user.role === 'customer' || user.usertype === 'rider';
        if (!isCustomer) return false;
        if (isSuper) return true;
        const userTid = user.tenant_id || user.tenantId;
        if (tId && userTid) {
          return userTid === tId;
        }
        if (profile?.uid && user.fleetadmin) {
          return user.fleetadmin === profile.uid;
        }
        return !tId && !userTid;
      }));
    }else{
      setData([]);
    }
    loaded.current = true;
  },[staticusers, auth.profile]);

  const columns = [
    { 
      title: 'Solicitante / Contato', 
      field: 'firstName',
      render: rowData => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar 
            src={rowData.profile_image} 
            sx={{ width: 40, height: 40, bgcolor: '#0f172a', fontSize: '0.875rem', fontWeight: 700 }}
          >
            {rowData.firstName?.[0] || 'C'}
          </Avatar>
          <Box>
            <Typography sx={{ fontWeight: 600, color: '#0f172a', fontSize: '0.875rem', lineHeight: 1.2 }}>
              {rowData.firstName} {rowData.lastName}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>
              {rowData.email || 'Sem e-mail'}
            </Typography>
          </Box>
        </Box>
      )
    },
    { 
      title: t('mobile') || 'Telefone', 
      field: 'mobile', 
      render: rowData => rowData.mobile || '—' 
    },
    { 
      title: t('account_approve') || 'Status',  
      field: 'approved', 
      type:'boolean',
      render: rowData => (
        <Chip
          size="small"
          label={rowData.approved ? 'Ativo' : 'Inativo'}
          sx={{
            bgcolor: rowData.approved ? '#dcfce7' : '#f1f5f9',
            color: rowData.approved ? '#15803d' : '#64748b',
            fontWeight: 700,
            fontSize: '0.75rem',
            borderRadius: '6px'
          }}
        />
      ),
      cellStyle: { textAlign: 'center' }
    },
    { 
      title: t('createdAt') || 'Cadastro', 
      field: 'createdAt', 
      defaultSort:'desc',
      render: rowData => rowData.createdAt ? moment(rowData.createdAt).format('DD/MM/YYYY') : '—',
      cellStyle: { fontSize: '0.8125rem', color: '#64748b' }
    },
    {
      title: 'Ações',
      field: 'actions',
      cellStyle: { textAlign: 'center', width: 110 },
      headerStyle: { textAlign: 'center', width: 110 },
      render: rowData => (
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
          <Tooltip title="Editar Solicitante">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setEditingUser(rowData);
              }}
              sx={{
                color: '#0284c7',
                bgcolor: '#f0f9ff',
                border: '1px solid #bae6fd',
                '&:hover': { bgcolor: '#e0f2fe' }
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Excluir Solicitante">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setDeleteConfirmUser(rowData);
              }}
              sx={{
                color: '#ef4444',
                bgcolor: '#fef2f2',
                border: '1px solid #fecaca',
                '&:hover': { bgcolor: '#fee2e2' }
              }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

    const [profileModal, setProfileModal] =  useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [imageData, setImageData] =  useState(false);
    const [selectedImage, setSelectedImage] = useState(null); 
    const [imageType, setImageType] = useState(null);
    const [userData, setUserData] = useState();
    const [loading, setLoading] = useState(false);
    const [commonAlert, setCommonAlert] = useState({ open: false, msg: '' });


  const handleCommonAlertClose = (e) => {
    e.preventDefault();
    setCommonAlert({ open: false, msg: '' })
  };

    const onClick = (rowData,text) => {
      if(text === 'verifyIdImage'){
       setImageType(text);
       setImageData(rowData.verifyIdImage);
     }
     setProfileModal(true);
     setUserData(rowData);
   };

   const handleProfileModal = (e) => {
    setProfileModal(false);
    setSelectedImage(null);
  }

   const handleSetProfileModal = (e) =>{
    e.preventDefault();
    if(selectedImage){
      setLoading(true);
      dispatch(updateLicenseImage(userData.id, selectedImage, imageType));
      setProfileModal(false); 
      setSelectedImage(null);
      setTimeout(()=>{
        setLoading(false); 
        dispatch(fetchUsersOnce());
      },10000);
    }
    else{
      setCommonAlert({ open: true, msg: t('choose_image_first') })
    }
  }

    return (
      !loaded.current? <CircularLoading/>:
      <div>
        <MaterialTable
          title=""
          columns={columns}
          style={modernTableStyle}
          data={data}
          onRowClick={((evt, selectedRow) => setSelectedRow(selectedRow.tableData.id))}
          options={modernTableOptions(selectedRow, {
            searchPlaceholder: "Buscar solicitante...",
            maxBodyHeight: "calc(100vh - 280px)"
          })}
          localization={{ body:{
            addTooltip: (t('add')),
            deleteTooltip: (t('delete')),
            editTooltip: (t('edit')),
            emptyDataSourceMessage: (
              (t('blank_message'))
          ),
            editRow: { 
              deleteText: (t('delete_message')),
              cancelTooltip: (t('cancel')),
              saveTooltip: (t('save')) 
              }, 
            },
            toolbar: {
              searchPlaceholder: (t('search')),
              exportTitle: (t('export')),
            },
            header: {
              actions: (t('actions')) 
          },
          pagination: {
            labelDisplayedRows: ('{from}-{to} '+ (t('of'))+ ' {count}'),
            firstTooltip: (t('first_page_tooltip')),
            previousTooltip: (t('previous_page_tooltip')),
            nextTooltip: (t('next_page_tooltip')),
            lastTooltip: (t('last_page_tooltip'))
          },
          }}
        />

        {/* Modal Unificado de Edição */}
        <UserCreateDialog
          open={Boolean(editingUser)}
          userToEdit={editingUser}
          onClose={() => setEditingUser(null)}
          onSuccess={() => {
            setEditingUser(null);
            dispatch(fetchUsersOnce());
          }}
          defaultRole="customer"
        />

        {/* Diálogo de Confirmação de Exclusão */}
        <Dialog
          open={Boolean(deleteConfirmUser)}
          onClose={() => setDeleteConfirmUser(null)}
          PaperProps={{ sx: { borderRadius: '16px', p: 1, maxWidth: 440 } }}
        >
          <DialogTitle sx={{ fontWeight: 700, color: '#0f172a' }}>
            Excluir Solicitante?
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ color: '#475569' }}>
              Deseja realmente remover o usuário <strong>{deleteConfirmUser?.firstName} {deleteConfirmUser?.lastName}</strong>? Esta ação revogará o acesso à plataforma.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button onClick={() => setDeleteConfirmUser(null)} sx={{ color: '#64748b' }}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="danger"
              onClick={() => {
                if (deleteConfirmUser?.id) {
                  dispatch(deleteUser(deleteConfirmUser.id));
                  setDeleteConfirmUser(null);
                  setTimeout(() => dispatch(fetchUsersOnce()), 600);
                }
              }}
              sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}
            >
              Excluir
            </Button>
          </DialogActions>
        </Dialog>

        <Modal
          disablePortal
          disableEnforceFocus
          disableAutoFocus
          open={profileModal}
          onClose={handleProfileModal}
          className={classes.modal}
          container={() => rootRef.current}
        >
          <Grid container spacing={1} className={classes.paper} style={{direction:isRTL==='rtl'?'rtl':'ltr'}}>
                <Grid item xs={12} sm={12} md={12} lg={12}>
                  <Typography component="h1" variant="h6">
                        {t('license_image')}
                        <input
                            type="file"
                            style={{marginLeft:10}}
                            name= {t('image')}
                            onChange={(event) => {
                                setSelectedImage(event.target.files[0]);
                            }}
                        />
                  </Typography>
                </Grid>
                  <Grid item xs={12} sm={12} md={12} lg={12}>
                    {selectedImage  && !loading ? 
                      <Tooltip title={t('cancel')}>
                        <CancelIcon onClick={()=>setSelectedImage(null)} style={{ fontSize: 30,backgroundColor:'red',borderRadius:50,color:"white" }}  />
                      </Tooltip>
                    : null }
                  </Grid>
                  <Grid item xs={12} sm={12} md={12} lg={12}>
                      {selectedImage ? 
                        <img alt="not fount" height={"200px"} src={URL.createObjectURL(selectedImage)} style={{marginTop:15,marginBottom:20}}/>
                      :
                        <img alt="licimage" height={"200px"} src={imageData} style={{marginTop:10}}/>
                      }
                      <br />
                    </Grid>
                  
            <Grid item xs={12} sm={12} md={12} lg={12} style={{textAlign:isRTL==='rtl'?'right':'left'}}>
              {loading ? 
              <Grid
                container
                spacing={0}
                alignItems="center"
                justify="center"
                style={{ minHeight: '5vh' }} >
                <CircularProgress/>
              </Grid>
              :
              <Grid item xs={12} sm={12} md={12} lg={12} style={{textAlign:isRTL==='rtl'?'right':'left'}}>
              <Button onClick={handleProfileModal} variant="contained" color="danger">
              {t('cancel')}
            </Button> 
              <Button onClick={handleSetProfileModal} variant="contained" color="secondaryButton" style={{marginLeft:10}}>
                {t('save')}
              </Button>
              </Grid>
              }
            </Grid>
          </Grid>
        </Modal>
        <AlertDialog open={commonAlert.open} onClose={handleCommonAlertClose}>{commonAlert.msg}</AlertDialog>
      </div>
    );
  }
